/**
 * FlahaSOIL public product surface.
 *
 * The page describes only capabilities implemented by the React v2
 * application. It remains API-independent so public product information is
 * available even when the backend cannot be reached.
 */
import { keyframes } from "@emotion/react";
import {
	Box,
	Button,
	Chip,
	Container,
	Divider,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HubIcon from "@mui/icons-material/Hub";
import IosShareIcon from "@mui/icons-material/IosShare";
import OpacityIcon from "@mui/icons-material/Opacity";
import ScienceIcon from "@mui/icons-material/Science";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useAuth } from "../auth";
import { FlahaEcosystemSection } from "../features/landing/components/FlahaEcosystemSection";
import { PlanTierCards } from "../features/pricing/PlanTierCards";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { FlahaLogo } from "../layouts/components/FlahaLogo";
import { GlassSurface } from "../layouts/components/GlassSurface";
import {
	BRAND_FOOTER,
	BRAND_HIERARCHY,
	BRAND_MARKETING_URL,
	BRAND_ORG,
	BRAND_PRODUCT_LABEL,
} from "../theme/brand";
import { reducedMotionMediaQuery } from "../theme/motionTokens";

interface Capability {
	title: string;
	description: string;
	icon: ReactNode;
}

interface ProvenanceLayer {
	label: string;
	description: string;
}

const reveal = keyframes`
	from { opacity: 0; transform: translate3d(0, 12px, 0); }
	to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const CAPABILITIES: Capability[] = [
	{
		title: "Soil physics",
		description:
			"Texture classification, field capacity, permanent wilting point, plant-available water, saturation, saturated conductivity, bulk density, porosity, and drainage-related classes.",
		icon: <OpacityIcon />,
	},
	{
		title: "Soil chemistry",
		description:
			"Laboratory pH and salinity evidence alongside CEC, base saturation, calcium, magnesium, potassium and sodium shares, ESP, SAR, and cation balance where the submitted test level supports them.",
		icon: <ScienceIcon />,
	},
	{
		title: "Agronomic interpretation",
		description:
			"Evidence-dependent classifications for pH, salinity, sodicity, water holding, drainage, cation balance, overall soil rating, and authoritative warnings.",
		icon: <FactCheckIcon />,
	},
	{
		title: "Professional traceability",
		description:
			"Project and sample context, calculation versions and traces, immutable report versions, evidence completeness, warnings, and authenticated PDF delivery.",
		icon: <AssessmentIcon />,
	},
];

const PROVENANCE_LAYERS: ProvenanceLayer[] = [
	{
		label: "Reported",
		description:
			"Laboratory or manually entered observations retained with the soil test.",
	},
	{
		label: "Calculated",
		description:
			"Outputs returned by the versioned soil-physics and soil-chemistry engines.",
	},
	{
		label: "Model-estimated",
		description:
			"Values derived from available evidence are identified separately from measurements.",
	},
	{
		label: "Classified",
		description:
			"Texture, condition, severity, and risk categories produced from authoritative results.",
	},
	{
		label: "Interpreted",
		description:
			"Agronomic meaning and warnings produced by the interpretation engine.",
	},
	{
		label: "Recommended",
		description:
			"Report actions are presented only when supported by the generated interpretation.",
	},
];

const DATA_FLOW = [
	"Project & sample context",
	"Laboratory evidence",
	"Authoritative calculation",
	"Classification & interpretation",
	"Versioned report or export projection",
];

export function LandingPage() {
	const { status } = useAuth();
	const theme = useTheme();
	const isWide = useMediaQuery(theme.breakpoints.up("md"));
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);
	const authenticated = status === "authenticated";
	const analysisRoute = authenticated
		? "/soil-tests/new"
		: "/login?next=%2Fsoil-tests%2Fnew";

	// Keep document title on the canonical default (DocumentMeta maps product
	// labels to SEO_DEFAULT_TITLE — never "FlahaSOIL v2").
	usePageHeader({
		title: "FlahaSOIL",
		subtitle: "Soil intelligence platform by Flaha Agri Tech",
	});

	return (
		<Box
			data-testid="landing-surface"
			data-layout={isWide ? "wide" : "compact"}
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			sx={{
				position: "relative",
				isolation: "isolate",
				overflow: "hidden",
				m: { xs: -2, sm: -3, lg: -4 },
				p: { xs: 2, sm: 3, lg: 4 },
				color: "workspace.textPrimary",
				background:
					"radial-gradient(circle at 10% 8%, rgba(119,169,87,0.14), transparent 28%), radial-gradient(circle at 88% 26%, rgba(74,156,197,0.13), transparent 25%), linear-gradient(145deg, #0C100E 0%, #17120F 58%, #101612 100%)",
			}}
		>
			<Container maxWidth="xl" disableGutters>
				<Box
					component="section"
					aria-labelledby="landing-hero-title"
					sx={{
						position: "relative",
						py: { xs: 5, sm: 7, lg: 10 },
						animation: `${reveal} ${theme.motion.duration.entry}ms ${theme.motion.easing.enter} both`,
						[reducedMotionMediaQuery]: {
							animation: "none",
							transform: "none",
						},
					}}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "minmax(0, 1fr)",
								lg: "minmax(0, 1.2fr) minmax(340px, 0.8fr)",
							},
							gap: { xs: 4, lg: 7 },
							alignItems: "center",
						}}
					>
						<Stack spacing={3} sx={{ minWidth: 0 }}>
							{/* Dual brand lockup: FlahaSOIL product + Flaha Agri Tech master */}
							<Stack spacing={1.25} alignItems="flex-start">
								<FlahaLogo size={72} variant="hero" color="#F6F0E3" />
								<Typography
									variant="caption"
									sx={{
										color: "workspace.textMuted",
										letterSpacing: "0.1em",
										textTransform: "uppercase",
										fontWeight: 600,
									}}
								>
									{BRAND_HIERARCHY}
								</Typography>
							</Stack>
							<Typography
								variant="overline"
								sx={{
									color: "workspace.green",
									letterSpacing: "0.14em",
									fontWeight: 700,
								}}
							>
								{BRAND_PRODUCT_LABEL}
							</Typography>
							<Typography
								id="landing-hero-title"
								component="h1"
								variant="h1"
								sx={{
									maxWidth: 900,
									fontSize: { xs: "2.5rem", sm: "3.75rem", lg: "5rem" },
									lineHeight: 1.02,
								}}
							>
								Scientific Soil Analysis, Interpretation and Decision Support
							</Typography>
							<Typography
								variant="h5"
								component="p"
								sx={{
									maxWidth: 760,
									color: "workspace.textSecondary",
									fontWeight: 450,
									lineHeight: 1.45,
								}}
							>
								From soil test data to explainable agronomic intelligence.
								Understand the soil. Identify the constraint. Act with evidence.
							</Typography>
							<Typography
								variant="body1"
								sx={{
									maxWidth: 720,
									color: "workspace.textSecondary",
									fontSize: { sm: "1.05rem" },
								}}
							>
								FlahaSOIL helps agronomists, laboratories, consultants, and land
								managers organize soil evidence, run supported analyses, interpret
								constraints, and preserve the path from source data to professional
								report.
							</Typography>

							<Stack
								direction="row"
								flexWrap="wrap"
								gap={1.5}
								aria-label="Primary actions"
							>
								<Button
									component={RouterLink}
									to={analysisRoute}
									variant="contained"
									size="large"
									endIcon={<ArrowForwardIcon />}
								>
									Start Soil Analysis
								</Button>
								{authenticated ? (
									<Button
										component={RouterLink}
										to="/dashboard"
										variant="outlined"
										size="large"
										sx={{ color: "workspace.textPrimary", borderColor: "workspace.borderStrong" }}
									>
										Open Dashboard
									</Button>
								) : (
									<>
										<Button
											component={RouterLink}
											to="/register"
											variant="outlined"
											size="large"
											sx={{ color: "workspace.textPrimary", borderColor: "workspace.borderStrong" }}
										>
											Create Account
										</Button>
										<Button
											component={RouterLink}
											to="/login"
											variant="text"
											size="large"
											sx={{ color: "workspace.textSecondary" }}
										>
											Sign In
										</Button>
									</>
								)}
								<Button
									component="a"
									href="#pricing"
									variant="outlined"
									size="large"
									sx={{ color: "workspace.textPrimary", borderColor: "workspace.borderStrong" }}
								>
									View plans & pricing
								</Button>
								<Button
									component={RouterLink}
									to="/standards"
									variant="text"
									size="large"
									sx={{ color: "workspace.blue" }}
								>
									Explore Scientific Standards
								</Button>
							</Stack>
						</Stack>

						<GlassSurface
							component="aside"
							aria-labelledby="data-flow-title"
							intensity="strong"
							depth="floating"
							sx={{ p: { xs: 2.5, sm: 3 }, minWidth: 0 }}
						>
							<Stack spacing={2.25}>
								<Stack direction="row" spacing={1.25} alignItems="center">
									<HubIcon sx={{ color: "workspace.blue" }} aria-hidden="true" />
									<Box>
										<Typography id="data-flow-title" component="h2" variant="h6">
											Traceable soil-intelligence flow
										</Typography>
										<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
											Each layer remains distinguishable.
										</Typography>
									</Box>
								</Stack>
								<Stack component="ol" spacing={1} sx={{ p: 0, m: 0, listStyle: "none" }}>
									{DATA_FLOW.map((step, index) => (
										<Stack
											component="li"
											key={step}
											direction="row"
											spacing={1.25}
											alignItems="center"
										>
											<Chip
												label={String(index + 1).padStart(2, "0")}
												size="small"
												sx={{
													color: "workspace.textPrimary",
													backgroundColor: "rgba(119,169,87,0.16)",
												}}
											/>
											<Typography variant="body2">{step}</Typography>
										</Stack>
									))}
								</Stack>
								<Divider sx={{ borderColor: "workspace.border" }} />
								<Stack direction="row" spacing={1.25} alignItems="flex-start">
									<IosShareIcon sx={{ color: "workspace.blue", mt: 0.25 }} aria-hidden="true" />
									<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
										The current FlahaCALC capability is a read-only export projection
										of selected authoritative soil outputs. It is not a full
										cross-application transfer.
									</Typography>
								</Stack>
							</Stack>
						</GlassSurface>
					</Box>
				</Box>

				<Box component="section" aria-labelledby="capabilities-title" sx={{ py: { xs: 5, md: 8 } }}>
					<Stack spacing={1} sx={{ mb: 3.5, maxWidth: 760 }}>
						<Typography variant="overline" sx={{ color: "workspace.green", letterSpacing: "0.12em" }}>
							Current React v2 capabilities
						</Typography>
						<Typography id="capabilities-title" component="h2" variant="h3">
							One evidence chain, from laboratory input to decision support
						</Typography>
						<Typography sx={{ color: "workspace.textSecondary" }}>
							Register to keep projects, samples, analyses, interpretations, and
							versioned reports inside an authenticated organization workspace.
						</Typography>
					</Stack>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "minmax(0, 1fr)",
								sm: "repeat(2, minmax(0, 1fr))",
								xl: "repeat(4, minmax(0, 1fr))",
							},
							gap: 2,
						}}
					>
						{CAPABILITIES.map((capability) => (
							<GlassSurface
								component="article"
								key={capability.title}
								intensity="standard"
								depth="raised"
								sx={{ p: 2.5, minWidth: 0, height: "100%" }}
							>
								<Box sx={{ color: "workspace.green", display: "flex", mb: 2 }} aria-hidden="true">
									{capability.icon}
								</Box>
								<Typography component="h3" variant="h6" sx={{ mb: 1 }}>
									{capability.title}
								</Typography>
								<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
									{capability.description}
								</Typography>
							</GlassSurface>
						))}
					</Box>
				</Box>

				<Box component="section" aria-labelledby="provenance-title" sx={{ py: { xs: 5, md: 8 } }}>
					<GlassSurface intensity="subtle" depth="raised" sx={{ p: { xs: 2.5, md: 4 } }}>
						<Stack spacing={1} sx={{ mb: 3 }}>
							<Typography variant="overline" sx={{ color: "workspace.blue", letterSpacing: "0.12em" }}>
								Explainable output provenance
							</Typography>
							<Typography id="provenance-title" component="h2" variant="h3">
								Know what was measured—and what was derived
							</Typography>
						</Stack>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: {
									xs: "minmax(0, 1fr)",
									sm: "repeat(2, minmax(0, 1fr))",
									lg: "repeat(3, minmax(0, 1fr))",
								},
								gap: 1.5,
							}}
						>
							{PROVENANCE_LAYERS.map((layer) => (
								<Box
									key={layer.label}
									sx={{
										minWidth: 0,
										p: 2,
										borderRadius: 1.5,
										border: "1px solid",
										borderColor: "workspace.border",
										backgroundColor: "rgba(246,240,227,0.035)",
									}}
								>
									<Typography component="h3" variant="subtitle1" sx={{ color: "workspace.textPrimary" }}>
										{layer.label}
									</Typography>
									<Typography variant="body2" sx={{ color: "workspace.textSecondary", mt: 0.5 }}>
										{layer.description}
									</Typography>
								</Box>
							))}
						</Box>
					</GlassSurface>
				</Box>

				<Box component="section" aria-labelledby="workflow-title" sx={{ py: { xs: 5, md: 8 } }}>
					<Stack spacing={1} sx={{ mb: 3.5, textAlign: { xs: "left", md: "center" } }}>
						<Typography variant="overline" sx={{ color: "workspace.green", letterSpacing: "0.12em" }}>
							Practical workflow
						</Typography>
						<Typography id="workflow-title" component="h2" variant="h3">
							Context before calculation. Evidence before action.
						</Typography>
					</Stack>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "minmax(0, 1fr)",
								md: "repeat(4, minmax(0, 1fr))",
							},
							gap: 1.5,
						}}
					>
						{[
							["01", "Organize", "Create the project and physical sample context."],
							["02", "Analyze", "Submit the supported laboratory or manual test evidence."],
							["03", "Interpret", "Review engine outputs, classifications, warnings, and evidence gaps."],
							["04", "Trace", "Generate a versioned professional report or inspect the FlahaCALC projection."],
						].map(([step, title, description]) => (
							<GlassSurface key={step} component="article" intensity="standard" depth="flat" sx={{ p: 2.5 }}>
								<Typography variant="overline" sx={{ color: "workspace.blue" }}>
									{step}
								</Typography>
								<Typography component="h3" variant="h6" sx={{ mt: 0.5 }}>
									{title}
								</Typography>
								<Typography variant="body2" sx={{ color: "workspace.textSecondary", mt: 1 }}>
									{description}
								</Typography>
							</GlassSurface>
						))}
					</Box>
				</Box>

				{/* Phase 11 — plan cards after Practical workflow (public shell hides sidebar). */}
				<Box
					id="pricing"
					sx={{ py: { xs: 5, md: 8 }, scrollMarginTop: 96 }}
				>
					<PlanTierCards headingId="landing-pricing-title" />
					<Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
						<Button component={RouterLink} to="/pricing" size="small">
							Open full pricing page →
						</Button>
					</Stack>
				</Box>

				<FlahaEcosystemSection />

				<Divider sx={{ mt: { xs: 6, md: 9 }, mb: 3, borderColor: "workspace.border" }} />
				<Stack
					component="footer"
					direction={{ xs: "column", sm: "row" }}
					justifyContent="space-between"
					gap={1}
					sx={{ pb: 2, color: "workspace.textMuted" }}
				>
					<Stack spacing={0.5}>
						<Typography variant="caption">{BRAND_FOOTER}</Typography>
						<Typography variant="caption">
							A product of {BRAND_ORG}.{" "}
							<Box
								component="a"
								href={BRAND_MARKETING_URL}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ color: "workspace.blue", textDecoration: "none" }}
							>
								Product overview on flaha.org
							</Box>
						</Typography>
					</Stack>
					<Typography variant="caption">
						Outputs remain traceable to available evidence and authoritative engines.
					</Typography>
				</Stack>
			</Container>
		</Box>
	);
}
